import { createApi, retry } from '@reduxjs/toolkit/query/react'
import config from '@/config'
import { buildUrl } from '@/utils/common'
import { axiosBaseQuery } from '../helpers'
import type {
  AnomalyType,
  ReportCustomerChartType,
  ReportCustomerTotalType,
  TransactionReport,
} from './reports.type'
import {
  type SegmentOverviewAdmin,
  type SegmentOverviewOrganisation,
  type PaginatedResponse,
  type SegmentOrganisation,
  SegmentUser,
  type SegmentUserDetail,
  type SegmentVenue,
  type SegmentVenueDetail,
  SegmentEvent,
  type SegmentPage,
  SegmentSession,
  type SegmentDateRangeParams,
  type SegmentPaginationParams,
  type SegmentEventsParams,
  type SegmentSessionsParams,
  type SegmentUsersParams,
  type SegmentVenuesParams,
  type SegmentPagesParams,
  type TimeSeriesResponse,
  type TimeSeriesParams,
  type InsightsResponse,
  type InsightsParams,
  type AggregationStatus,
  type AggregationRunResponse,
  type SegmentUserEnhanced,
  type SegmentSessionEnhanced,
  type SegmentEventEnhanced,
  type ActivityInsightsResponse,
  type ActivityInsightsParams,
  type RevenueUsageCorrelationResponse,
  type RevenueUsageCorrelationParams,
} from './segment-analytics.types'
import {
  type StripeOverviewResponse,
  type StripeAdminOverviewResponse,
  type StripeSubscriptionsResponse,
  type StripeSubscriptionsParams,
  type StripeRevenueResponse,
  type StripeRevenueParams,
  type StripeOrganisationsParams,
  type StripeFeatureValueResponse,
  type StripeInvoicesResponse,
  type StripeInvoicesParams,
  type StripeProductsResponse,
  type StripeProductsParams,
  StripeAdminMetrics,
  type StripeOrganisationsResponse,
  StripeOrganisation,
} from './stripe-analytics.types'
import {
  type OverviewParams,
  type OverviewResponse,
  type BoardParams,
  type BoardResponse,
  type BoardCountsResponse,
  type BoardPaginatedResponse,
  type AccountDetailsResponse,
  type CalculateRequest,
  type CalculateResponse,
  type ResolveAlertResponse,
  type HealthStatus,
  type UpdateStatusResponse,
} from './account-health.types'

const service = 'reports'

const reportsApi = createApi({
  reducerPath: service,

  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: config.url.reports,
      service,
    }),
    {
      maxRetries: 5,
    }
  ),
  endpoints: (build) => ({
    getBenchmarkAverages: build.query<
      {
        _sum: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
        }
        _count: number
        _max: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
          organisation_id: string
        }
        _min: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
          organisation_id: string
        }
        _avg: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
        }
      },
      void
    >({
      query: () => ({
        method: 'get',
        url: '/benchmark/averages',
      }),
    }),

    getAdminTotals: build.query<
      {
        total_customers_count: number
        new_customer_count: number
        interaction_count: number
        marketing_email_sent_count: number
        marketing_email_delivered_count: number
        marketing_email_opt_in_count: number
        marketing_sms_opt_in_count: number
        marketing_data_opt_in_count: number
        inbox_threads_created_count: number
        return_visits_from_marketing_count: number
      },
      void
    >({
      query: () => ({
        method: 'get',
        url: '/totals',
      }),
    }),
    getAnomalies: build.query<AnomalyType[], void>({
      query: () => ({
        url: `/anomaly/anomalies`,
        method: 'GET',
      }),
    }),
    getTransaction: build.query<
      TransactionReport,
      { org_id: string; serial?: string }
    >({
      query: ({ org_id, serial }) => ({
        url: buildUrl(`/${org_id}/transactions`, { serial }),
        method: 'GET',
      }),
    }),
    getTransactionBySource: build.query<
      {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      },
      { org_id: string; serial?: string }
    >({
      query: ({ org_id, serial }) => ({
        url: buildUrl(`/${org_id}/transactions/source`, { serial }),
        method: 'GET',
      }),
    }),
    getTransactionBySourceAndTime: build.query<
      TransactionReport,
      { org_id: string; start: Date; end: Date; serial?: string }
    >({
      query: ({ org_id, start, end, serial }) => ({
        url: buildUrl(`/${org_id}/transactions/time/source`, {
          start,
          end,
          serial,
        }),
        method: 'GET',
      }),
    }),
    getTransactionByTime: build.query<
      TransactionReport,
      { org_id: string; start: Date; end: Date; serial?: string }
    >({
      query: ({ org_id, start, end, serial }) => ({
        url: buildUrl(`/${org_id}/transactions/time`, { start, end, serial }),
        method: 'GET',
      }),
    }),
    getCustomers: build.query<
      {
        totals: ReportCustomerTotalType
      },
      { org_id: string }
    >({
      query: ({ org_id }) => ({
        url: `/${org_id}/customers`,
        method: 'GET',
      }),
    }),
    getCustomersByTime: build.query<
      {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      },
      { org_id: string; start: Date; end: Date; serial?: string }
    >({
      query: ({ org_id, start, end, serial }) => ({
        url: buildUrl(`/${org_id}/customers/time`, { start, end, serial }),
        method: 'GET',
      }),
    }),
    getCustomersBySource: build.query<
      {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      },
      {
        org_id: string
        serial?: string
        start: Date
        end: Date
        source?: string
      }
    >({
      query: ({ org_id, serial, start, end, source }) => ({
        url: buildUrl(`/${org_id}/customers/source`, {
          serial,
          start,
          end,
          source,
        }),
        method: 'GET',
      }),
      transformResponse: (response: {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      }) => {
        return {
          ...response,
          chart: [...response.chart].sort(
            (a, b) => b.interaction_count - a.interaction_count
          ),
        }
      },
    }),
    getDataBySource: build.query<
      {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      },
      {
        org_id: string
        serial?: string
        start: Date
        end: Date
        source?: string
      }
    >({
      query: ({ org_id, serial, start, end, source }) => ({
        url: buildUrl(`/${org_id}/source/time`, { serial, start, end, source }),
        method: 'GET',
      }),
      transformResponse: (response: {
        totals: ReportCustomerTotalType
        chart: ReportCustomerChartType[]
      }) => {
        return {
          ...response,
          chart: [...response.chart].sort(
            (a, b) => b.interaction_count - a.interaction_count
          ),
        }
      },
    }),
    getBenchmarkThirtyDayRolling: build.query<
      {
        _sum: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
        }
        _count: number
        _max: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
          serial: string
        }
        _min: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
          serial: string
        }
        _avg: {
          in_venue_visits_count: number
          total_customers_count: number
          new_customer_count: number
          interaction_count: number
          marketing_email_sent_count: number
          marketing_email_delivered_count: number
          marketing_email_opt_in_count: number
          marketing_sms_opt_in_count: number
          marketing_data_opt_in_count: number
          inbox_threads_created_count: number
          return_visits_from_marketing_count: number
        }
      },
      void
    >({
      query: () => ({
        method: 'get',
        url: '/benchmark/thirty-day-rolling',
      }),
    }),
  }),
})

// Segment Analytics endpoints
const segmentApi = reportsApi.injectEndpoints({
  endpoints: (build) => ({
    // Overview endpoints
    getSegmentOverview: build.query<
      SegmentOverviewOrganisation,
      { orgId: string } & SegmentDateRangeParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(`/${orgId}/segment-analytics/overview`, params),
      }),
    }),

    getSegmentAdminOverview: build.query<
      SegmentOverviewAdmin | SegmentOverviewOrganisation,
      SegmentDateRangeParams & { organisationId?: string }
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/segment-analytics/overview', params),
      }),
    }),

    // Organisations (admin only)
    getSegmentOrganisations: build.query<
      PaginatedResponse<SegmentOrganisation>,
      SegmentDateRangeParams & SegmentPaginationParams
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/segment-analytics/organisations', params),
      }),
    }),

    // Users endpoints
    getSegmentUsers: build.query<
      PaginatedResponse<SegmentUserEnhanced>,
      { orgId?: string } & SegmentUsersParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/users`
            : '/admin/segment-analytics/users',
          params
        ),
      }),
    }),

    getSegmentUserDetail: build.query<
      SegmentUserDetail,
      { userId: string; orgId?: string } & SegmentDateRangeParams
    >({
      query: ({ userId, orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/users/${userId}`
            : `/admin/segment-analytics/users/${userId}`,
          params
        ),
      }),
    }),

    // Venues endpoints
    getSegmentVenues: build.query<
      PaginatedResponse<SegmentVenue>,
      { orgId?: string } & SegmentVenuesParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/venues`
            : '/admin/segment-analytics/venues',
          params
        ),
      }),
    }),

    getSegmentVenueDetail: build.query<
      SegmentVenueDetail,
      { serial: string; orgId?: string } & SegmentDateRangeParams
    >({
      query: ({ serial, orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/venues/${serial}`
            : `/admin/segment-analytics/venues/${serial}`,
          params
        ),
      }),
    }),

    // Events endpoint
    getSegmentEvents: build.query<
      PaginatedResponse<SegmentEventEnhanced>,
      { orgId?: string } & SegmentEventsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/events`
            : '/admin/segment-analytics/events',
          params
        ),
      }),
    }),

    // Pages endpoint
    getSegmentPages: build.query<
      {
        pages: SegmentPage[]
        dateRange: { startDate: string; endDate: string }
      },
      { orgId?: string } & SegmentPagesParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/pages`
            : '/admin/segment-analytics/pages',
          params
        ),
      }),
    }),

    // Sessions endpoint
    getSegmentSessions: build.query<
      PaginatedResponse<SegmentSessionEnhanced>,
      { orgId?: string } & SegmentSessionsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/sessions`
            : '/admin/segment-analytics/sessions',
          params
        ),
      }),
    }),

    // Time Series Metrics endpoint
    getSegmentTimeSeries: build.query<
      TimeSeriesResponse,
      { orgId?: string } & TimeSeriesParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/metrics/time-series`
            : '/admin/segment-analytics/metrics/time-series',
          params
        ),
      }),
    }),

    // Insights Dashboard endpoint
    getSegmentInsights: build.query<
      InsightsResponse,
      { orgId?: string } & InsightsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/insights`
            : '/admin/segment-analytics/insights',
          params
        ),
      }),
    }),

    // Aggregation Status endpoint
    getAggregationStatus: build.query<AggregationStatus, void>({
      query: () => ({
        method: 'get',
        url: '/admin/segment-analytics/aggregation/status',
      }),
    }),

    // Run Aggregation endpoint
    runAggregation: build.mutation<AggregationRunResponse, { force?: boolean }>(
      {
        query: (params: any) => ({
          method: 'post',
          url: buildUrl('/admin/segment-analytics/aggregation/run', params),
        }),
      }
    ),

    // Activity Insights endpoint
    getSegmentActivityInsights: build.query<
      ActivityInsightsResponse,
      { orgId?: string } & ActivityInsightsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/activity-insights`
            : '/admin/segment-analytics/activity-insights',
          params
        ),
      }),
    }),

    // Revenue-Usage Correlation endpoint
    getRevenueUsageCorrelation: build.query<
      RevenueUsageCorrelationResponse,
      { orgId?: string } & RevenueUsageCorrelationParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/segment-analytics/revenue-usage-correlation`
            : '/admin/segment-analytics/revenue-usage-correlation',
          params
        ),
      }),
    }),
  }),
})

export const {
  useGetSegmentOverviewQuery,
  useGetSegmentAdminOverviewQuery,
  useGetSegmentOrganisationsQuery,
  useGetSegmentUsersQuery,
  useGetSegmentUserDetailQuery,
  useGetSegmentVenuesQuery,
  useGetSegmentVenueDetailQuery,
  useGetSegmentEventsQuery,
  useGetSegmentPagesQuery,
  useGetSegmentSessionsQuery,
  useGetSegmentTimeSeriesQuery,
  useGetSegmentInsightsQuery,
  useGetAggregationStatusQuery,
  useRunAggregationMutation,
  useGetSegmentActivityInsightsQuery,
  useGetRevenueUsageCorrelationQuery,
} = segmentApi

// Stripe Analytics endpoints
const stripeApi = reportsApi.injectEndpoints({
  endpoints: (build) => ({
    // Overview endpoint
    getStripeOverview: build.query<StripeOverviewResponse, { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/${orgId}/stripe-analytics/overview`,
      }),
    }),

    getStripeAdminOverview: build.query<
      StripeAdminOverviewResponse,
      { organisationId?: string }
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/stripe-analytics/overview', params),
      }),
    }),

    // Organisations endpoint (admin only)
    getStripeOrganisations: build.query<
      StripeOrganisationsResponse,
      StripeOrganisationsParams
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/stripe-analytics/organisations', params),
      }),
    }),

    // Subscriptions endpoint
    getStripeSubscriptions: build.query<
      StripeSubscriptionsResponse,
      { orgId?: string } & StripeSubscriptionsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/stripe-analytics/subscriptions`
            : '/admin/stripe-analytics/subscriptions',
          params
        ),
      }),
    }),

    // Revenue Analytics endpoint
    getStripeRevenue: build.query<
      StripeRevenueResponse,
      { orgId?: string } & StripeRevenueParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/stripe-analytics/revenue`
            : '/admin/stripe-analytics/revenue',
          params
        ),
      }),
    }),

    // Feature Value endpoint
    getStripeFeatureValue: build.query<
      StripeFeatureValueResponse,
      { orgId?: string } & StripeRevenueParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/stripe-analytics/feature-value`
            : '/admin/stripe-analytics/feature-value',
          params
        ),
      }),
    }),

    // Invoices endpoint
    getStripeInvoices: build.query<
      StripeInvoicesResponse,
      { orgId?: string } & StripeInvoicesParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/stripe-analytics/invoices`
            : '/admin/stripe-analytics/invoices',
          params
        ),
      }),
    }),

    // Products endpoint
    getStripeProducts: build.query<
      StripeProductsResponse,
      { orgId?: string } & StripeProductsParams
    >({
      query: ({ orgId, ...params }) => ({
        method: 'get',
        url: buildUrl(
          orgId
            ? `/${orgId}/stripe-analytics/products`
            : '/admin/stripe-analytics/products',
          params
        ),
      }),
    }),
  }),
})

export const {
  useGetStripeOverviewQuery,
  useGetStripeAdminOverviewQuery,
  useGetStripeOrganisationsQuery,
  useGetStripeSubscriptionsQuery,
  useGetStripeRevenueQuery,
  useGetStripeFeatureValueQuery,
  useGetStripeInvoicesQuery,
  useGetStripeProductsQuery,
} = stripeApi

// Account Health endpoints
const accountHealthApi = reportsApi.injectEndpoints({
  endpoints: (build) => ({
    // Overview endpoint
    getAccountHealthOverview: build.query<OverviewResponse, OverviewParams>({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/account-health/overview', params),
      }),
    }),

    // Board counts endpoint (no status parameter)
    getAccountHealthBoardCounts: build.query<
      BoardCountsResponse,
      Omit<BoardParams, 'status'>
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/account-health/board', params),
      }),
    }),

    // Board data endpoint (with status parameter)
    getAccountHealthBoardData: build.query<
      BoardPaginatedResponse,
      BoardParams & { status: HealthStatus }
    >({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/account-health/board', params),
      }),
    }),

    // Legacy board endpoint for backwards compatibility
    getAccountHealthBoard: build.query<BoardResponse, BoardParams>({
      query: (params: any) => ({
        method: 'get',
        url: buildUrl('/admin/account-health/board', params),
      }),
    }),

    // Account details endpoint
    getAccountHealthDetails: build.query<
      AccountDetailsResponse,
      { organisationId: string }
    >({
      query: ({ organisationId }) => ({
        method: 'get',
        url: `/admin/account-health/${organisationId}`,
      }),
    }),

    // Calculate health endpoint
    calculateAccountHealth: build.mutation<CalculateResponse, CalculateRequest>(
      {
        query: (body: any) => ({
          method: 'post',
          url: '/admin/account-health/calculate',
          data: body,
        }),
      }
    ),

    // Resolve alert endpoint
    resolveAccountHealthAlert: build.mutation<
      ResolveAlertResponse,
      { alertId: string }
    >({
      query: ({ alertId }) => ({
        method: 'post',
        url: `/admin/account-health/alerts/${alertId}/resolve`,
      }),
    }),

    // Update account health status
    updateAccountHealthStatus: build.mutation<
      UpdateStatusResponse,
      { organisationId: string; status: HealthStatus; reason?: string }
    >({
      query: ({ organisationId, status, reason }) => ({
        method: 'patch',
        url: `/admin/account-health/${organisationId}/status`,
        data: { status, reason },
      }),
    }),
  }),
})

export const {
  useGetAccountHealthOverviewQuery,
  useGetAccountHealthBoardQuery,
  useGetAccountHealthBoardCountsQuery,
  useGetAccountHealthBoardDataQuery,
  useGetAccountHealthDetailsQuery,
  useCalculateAccountHealthMutation,
  useResolveAccountHealthAlertMutation,
  useUpdateAccountHealthStatusMutation,
} = accountHealthApi

// Spatial reporting endpoints — currently just the grid heatmap that powers
// the global customer-density map and the venue-level overlay on the
// access-points page.
export interface HeatmapMarkerPoint {
  lat: number
  lng: number
  intensity: number
  count: number
  metrics: {
    totalInteractions: number
    totalPennyValue: number
  }
}

export interface HeatmapMarkersResponse {
  heatmap: HeatmapMarkerPoint[]
  meta?: {
    startDate: string
    endDate: string
    count: number
    gridSize: number
    source?: 'all' | 'venues' | 'accessPoints'
    serial?: string
    bounds: {
      minLat: number
      maxLat: number
      minLng: number
      maxLng: number
    }
    intensityRange: { min: number; max: number }
  }
}

export interface HeatmapFrame {
  time: string
  heatmap: HeatmapMarkerPoint[]
  intensityRange: { min: number; max: number }
  totalInteractions: number
}

export interface HeatmapMarkerFramesResponse {
  frames: HeatmapFrame[]
  meta?: {
    startDate: string
    endDate: string
    frameCount: number
    pointCount: number
    gridSize: number
    source: 'accessPoints'
    serial?: string
    intensityRange: { min: number; max: number }
  }
}

const mapsReportsApi = reportsApi.injectEndpoints({
  endpoints: (build) => ({
    getHeatmapMarkers: build.query<
      HeatmapMarkersResponse,
      {
        startTime: string
        endTime: string
        gridSize: number
        organisationId?: string
        serial?: string
        source?: 'all' | 'venues' | 'accessPoints'
        intensityType?:
          | 'default'
          | 'interactions'
          | 'pennyValue'
          | 'venueCount'
          | 'custom'
        bounds: {
          minLat: number
          maxLat: number
          minLng: number
          maxLng: number
        }
      }
    >({
      query: ({
        startTime,
        endTime,
        gridSize,
        organisationId,
        serial,
        source,
        intensityType = 'interactions',
        bounds,
      }) => ({
        method: 'get',
        url: buildUrl('/maps/heatmap-markers', {
          startTime,
          endTime,
          gridSize,
          organisationId,
          serial,
          source,
          intensityType,
          bounds: JSON.stringify(bounds),
        }),
      }),
    }),
    getHeatmapMarkerFrames: build.query<
      HeatmapMarkerFramesResponse,
      {
        startTime: string
        endTime: string
        gridSize: number
        organisationId?: string
        serial?: string
        bounds: {
          minLat: number
          maxLat: number
          minLng: number
          maxLng: number
        }
      }
    >({
      query: ({
        startTime,
        endTime,
        gridSize,
        organisationId,
        serial,
        bounds,
      }) => ({
        method: 'get',
        url: buildUrl('/maps/heatmap-markers/hourly', {
          startTime,
          endTime,
          gridSize,
          organisationId,
          serial,
          bounds: JSON.stringify(bounds),
        }),
      }),
    }),
  }),
})

export const { useGetHeatmapMarkersQuery, useGetHeatmapMarkerFramesQuery } =
  mapsReportsApi

export default reportsApi
